import type { Infer } from 'superstruct';
import {
	array,
	assign,
	nullable,
	number,
	object,
	optional,
	partial,
	pick,
	record,
	string,
} from 'superstruct';

import { ViewGoogleAdsCampaignTemplateDto } from '@adflow/shared/data-google-ads';
import { ViewMediaDto } from '@adflow/shared/data-media';
import { IdObjectWithName } from '@adflow/shared/util-superstruct';

import { historyArrayItem } from '../history-array-item';
import { IdMapStruct } from '../id-map';

import { HistoryGoogleAdsBudgetDto } from './history-google-ads-budget.dto';
import { HistoryGoogleAdsGroupDto } from './history-google-ads-group.dto';
import { HistoryGoogleAdsLocationDto } from './history-google-ads-location.dto';

// Map: avbId -> { online?: { category?: string; actions?: string[] }; offline?: string[] }
export const HistoryGoogleAdsConversionGoalSelectionDto = partial(
	object({
		online: partial(
			object({
				category: string(),
				actions: array(string()),
			})
		),
		offline: array(string()),
	})
);

export const HistoryGoogleAdsFormConversionGoalDto = record(
	string(),
	HistoryGoogleAdsConversionGoalSelectionDto
);
export type HistoryGoogleAdsFormConversionGoalDto = Infer<
	typeof HistoryGoogleAdsFormConversionGoalDto
>;

export const HistoryGoogleAdsCampaignDto = assign(
	partial(ViewGoogleAdsCampaignTemplateDto),
	partial(
		object({
			budgets: IdMapStruct(HistoryGoogleAdsBudgetDto),
			locations: IdMapStruct(HistoryGoogleAdsLocationDto),
			mobileAppCategories: array(historyArrayItem(IdObjectWithName)),
			promotionAssets: array(historyArrayItem(IdObjectWithName)),
			images: array(
				historyArrayItem(
					pick(ViewMediaDto, ['id', 'name', 'url', 'thumbnailUrl'])
				)
			),
			structuredSnippets: array(
				historyArrayItem(
					partial(
						object({
							id: number(),
							header: string(),
							values: array(historyArrayItem(string())),
						})
					)
				)
			),
			sitelinks: array(
				historyArrayItem(
					partial(
						object({
							id: number(),
							linkText: string(),
							finalUrl: string(),
							description1: optional(nullable(string())),
							description2: optional(nullable(string())),
						})
					)
				)
			),
			calloutAssets: array(historyArrayItem(string())),
			rsaCampaignHeadlines: array(historyArrayItem(string())),
			rsaCampaignDescriptions: array(historyArrayItem(string())),
			aiMaxTermExclusions: array(historyArrayItem(string())),
			aiMaxMessagingRestrictions: array(historyArrayItem(string())),
			groupTemplates: array(historyArrayItem(HistoryGoogleAdsGroupDto)),
			conversionGoals: HistoryGoogleAdsFormConversionGoalDto,
		})
	)
);

export type HistoryGoogleAdsCampaignDto = Infer<
	typeof HistoryGoogleAdsCampaignDto
>;
